import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence } from 'framer-motion'
import { CircleSlash2, Info, Repeat, SquareCheckBig, Tally5, Trash2 } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { DecorativeWave } from '@/src/renderer/components/ui/DecorativeWave'
import { AutoHeight } from '@/src/renderer/components/ui/AutoHeight'
import { NumberStepper } from '@/src/renderer/components/ui/NumberStepper'
import type { NthWeekday, RecurrenceEnd, RecurrenceRule, RecurrenceUnit, QuotaPeriod, Weekday } from '@/src/renderer/lib/db/models'
import {
  defaultEnd, describeRecurrence, isValidRule, monthName, nthKey, presetOf, recurrenceTone, ruleForPreset, weekdayName,
  RECURRENCE_PRESET_GROUPS, type RecurrencePreset, type RecurrenceTone,
} from '@/src/renderer/lib/recurrence'
import {
  RecurrenceDailyIcon, RecurrenceWeeklyIcon, RecurrenceMonthlyIcon, RecurrenceYearlyIcon,
} from '../../icons'
import { escapeBelongsToTaskDetails } from '../../utils/inlineEscape'
import { PickerSelect, type PickerSelectGroup } from './PickerSelect'
import { DatePickerField } from './DatePickerField'
import type { RecurrenceDropdownContentProps } from './RecurrenceDropdown.types'
import './RecurrenceDropdown.scss'

const UNITS: RecurrenceUnit[] = ['day', 'week', 'month', 'year']
const PERIODS: QuotaPeriod[] = ['week', 'month', 'year']
const WEEKDAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6]
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const NTHS: NthWeekday['nth'][] = [1, 2, 3, 4, -1]
const PRESET_FOR_UNIT: Record<RecurrenceUnit, RecurrencePreset> = { day: 'daily', week: 'weekly', month: 'monthly', year: 'yearly' }
/** Der Eintrag „Nie" — keine Voreinstellung, sondern das Fehlen einer Regel. */
const NEVER = 'never'
const END_KINDS = [NEVER, 'count', 'date'] as const

/** Die Regel mit diesem Ende — `undefined` nimmt es ab („Nie“), statt ein leeres Feld zu speichern. */
const withEnd = (rule: RecurrenceRule, end: RecurrenceEnd | undefined): RecurrenceRule => {
  const { end: _old, ...rest } = rule
  return (end ? { ...rest, end } : rest) as RecurrenceRule
}

const GROUP_LABEL_KEY: Record<RecurrenceTone, string> = {
  fixed: 'recurrence.group.fixed',
  quota: 'recurrence.group.quota',
  'after-done': 'recurrence.group.afterDone',
}

// Die vier festen Rhythmen tragen je ihren eigenen Kalender — sechs, vier, zwei, ein
// Punkt (`RecurrenceCalendarIcons`). Je seltener, desto leerer das Blatt.
const PRESET_ICON: Record<RecurrencePreset, React.ReactNode> = {
  daily: <RecurrenceDailyIcon size={16} />,
  weekly: <RecurrenceWeeklyIcon size={16} />,
  monthly: <RecurrenceMonthlyIcon size={16} />,
  monthlyWeekday: <RecurrenceMonthlyIcon size={16} />,
  yearly: <RecurrenceYearlyIcon size={16} />,
  quota: <Tally5 size={16} />,
  afterDone: <SquareCheckBig size={16} />,
}

/** Die Regel als Zeichenkette — zum Vergleichen. Schlüssel sortiert, damit die Reihenfolge nicht zählt. */
const stampOf = (rule: RecurrenceRule | null): string =>
  rule ? JSON.stringify(Object.fromEntries(Object.entries(rule).sort(([a], [b]) => a.localeCompare(b)))) : ''

/**
 * **Der Wiederkehr-Wähler** (CONTEXT.md, Wiederholung) — hinter der Menüzeile
 * **Wiederkehrend …**, hinter der **Marke** und offen in den **Aufgaben-Details**.
 *
 * Oben EINE Zeile „Wiederholen: [ … ]" mit einem Auswahlfeld, dessen Einträge nach den
 * drei Mechanismen gruppiert sind; darunter, hinter einer Wellenlinie, der Bauplatz,
 * dessen Zeilen der Wahl folgen; darunter der Satz, der aus den Feldern entsteht, im Ton
 * seines Mechanismus (Figma `5372:177968`). Bis 2026-09-19 standen oben drei
 * Chip-Gruppen — rund 250px, die das Auswahlfeld in einer Zeile sagt.
 *
 * **Das Auswahlfeld ist abgeleitet** (`presetOf`): Es liest die Regel, es hält sie nicht.
 * Wer unten die Einheit auf „Monat" stellt, hat oben „Monatlich" gewählt.
 *
 * **Zwei Arten zu schreiben.** In den Popovern ist alles Entwurf bis „Bestätigen". In
 * den Aufgaben-Details (`inline`) wird jede GÜLTIGE Änderung sofort geschrieben, einen
 * Fuß gibt es nicht (ADR 0026).
 *
 * **„Nie" ist der Weg zurück** — überall, wo der Aufrufer `onClear` reicht. Es wirkt
 * SOFORT, auch im Popover: An „Nie" gibt es nichts zu bauen, also auch nichts zu
 * bestätigen. Es schließt aber nichts; ob der Kasten danach zugeht, entscheidet allein
 * der Aufrufer — das Aufgaben-Menü lässt ihn bewusst offen, damit Abnehmen und neu Wählen
 * EIN Gedanke bleiben. Wo „Nie" fehlt, trägt die Marke den Papierkorb.
 *
 * **Natives `<select>` in den Popovern, Radix in den Details** — siehe `PickerSelect`.
 *
 * @example
 * <RecurrenceDropdownContent value={task.recurrence} seedDate={task.startDate}
 *   onValueChange={rule => save(rule)} onClose={close} placement="actionbar" />
 */
export const RecurrenceDropdownContent: React.FC<RecurrenceDropdownContentProps> = ({
  value, seedDate, onValueChange, onClose, onRemove, onClear, placement = 'actionbar',
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const inline = placement === 'inline'
  const seed = useMemo(() => seedDate ?? new Date(), [seedDate])
  /*
    **Ohne Wert zeigt der Kasten, was IST — also „Nie"** (Nutzer-Vorgabe, 2026-09-19).

    Bis dahin schlug er außerhalb der Aufgaben-Details „Wöchentlich" vor; der Gedanke war,
    dass man den Wähler öffnet, um eine Wiederholung zu setzen. Das stimmt, aber er
    behauptete damit etwas über die Aufgabe, was nicht dastand: Ein Blick ins Menü
    hinterließ den Eindruck, sie wiederhole sich wöchentlich. Gewählt wird jetzt danach.

    Der Vorschlag bleibt nur, wo es kein „Nie" gibt (kein `onClear`) — dort fehlte sonst
    jeder Eintrag, der „keine Wiederholung" überhaupt darstellen könnte, und das
    Auswahlfeld stünde leer.
  */
  const [draft, setDraft] = useState<RecurrenceRule | null>(
    () => value ?? (onClear ? null : ruleForPreset('weekly', seed)),
  )

  /** Der zuletzt geschriebene ODER übernommene Wert — hält den eigenen, zurückkehrenden vom fremden auseinander. */
  const lastWritten = useRef(stampOf(value))

  /**
   * Den Entwurf setzen — und in `inline` sofort schreiben, wenn er gültig ist. Eine
   * ungültige Zwischenstufe bleibt Entwurf: An der Aufgabe stünde sonst eine Regel, die
   * nie greift.
   */
  const apply = useCallback((next: RecurrenceRule) => {
    setDraft(next)
    if (!inline || !isValidRule(next)) return
    const stamp = stampOf(next)
    if (stamp === lastWritten.current) return
    lastWritten.current = stamp
    onValueChange(next)
  }, [inline, onValueChange])

  /*
    **Ein Wert von außen wird übernommen** (Marke an der Zeile, Aufgaben-Menü, eine andere
    Aufgabe in derselben Auswahl). Bis zur Etappe 4 baute `TaskDetails` den Bereich dafür
    neu auf; mit dem Sofort-Schreiben wäre JEDE eigene Änderung ein Neuaufbau, und der
    risse den Caret aus dem Zahlenfeld.
  */
  const valueStamp = stampOf(value)
  useEffect(() => {
    if (!inline || valueStamp === lastWritten.current) return
    lastWritten.current = valueStamp
    setDraft(value)
  }, [inline, valueStamp]) // eslint-disable-line react-hooks/exhaustive-deps

  const pick = useCallback((choice: string) => {
    if (choice === NEVER) {
      // **„Nie" wirkt sofort — auch im Popover.** Eine Regel baut man und bestätigt sie
      // dann; an „Nie" gibt es nichts zu bauen. Ein „Bestätigen" unter einem leeren Kasten
      // fragte nach, was schon gesagt ist — der Papierkorb an der Marke fragt auch nicht.
      setDraft(null)
      lastWritten.current = ''
      onClear?.()
      return
    }
    // Das Ende gehört nicht zum Rhythmus — es bleibt, wenn man die Art wechselt.
    apply(withEnd(ruleForPreset(choice as RecurrencePreset, seed), draft?.end))
  }, [apply, draft?.end, onClear, seed])

  /** Einheit wechseln, Intervall behalten — Tage und Pillen kommen frisch aus der Saat. */
  const setUnit = useCallback((unit: RecurrenceUnit) => {
    if (!draft || draft.type === 'quota') return
    if (draft.type === 'after_done') { apply({ ...draft, unit }); return }
    const next = ruleForPreset(PRESET_FOR_UNIT[unit], seed)
    apply(withEnd(next.type === 'fixed' ? { ...next, interval: draft.interval } : next, draft.end))
  }, [draft, apply, seed])

  /**
   * **Der letzte Wochentag lässt sich nicht abwählen.** Eine Wochenregel ohne Tag ist
   * ungültig und bliebe in den Details ungeschrieben — der Kasten zeigte dann etwas
   * anderes, als an der Aufgabe steht.
   */
  const toggleWeekday = useCallback((day: Weekday) => {
    if (!draft || draft.type !== 'fixed' || draft.unit !== 'week') return
    const has = draft.weekdays.includes(day)
    if (has && draft.weekdays.length === 1) return
    apply({ ...draft, weekdays: has ? draft.weekdays.filter(x => x !== day) : [...draft.weekdays, day].sort((a, b) => a - b) })
  }, [draft, apply])

  const setEndKind = useCallback((kind: string) => {
    if (!draft) return
    apply(withEnd(draft, kind === NEVER ? undefined : defaultEnd(kind as RecurrenceEnd['kind'], seed)))
  }, [draft, apply, seed])

  const valid = draft != null && isValidRule(draft)
  /**
   * **Ob es etwas zu bestätigen gibt.** Wer im Aufgaben-Menü nur nachsieht, was
   * eingestellt ist, soll keinen scharfen Knopf unter einer unveränderten Regel finden —
   * er verspräche eine Handlung, die nichts täte (Nutzer-Vorgabe, 2026-09-19). Ohne Wert
   * ist schon der Vorschlag („Wöchentlich") eine Änderung gegenüber „keine Wiederholung".
   */
  const changed = stampOf(draft) !== stampOf(value)
  const confirm = useCallback(() => { if (draft && valid) onValueChange(draft) }, [draft, valid, onValueChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // In den Aufgaben-Details gehört Escape der Spalte — vor dem
    // `stopPropagation` hinauslassen.
    if (escapeBelongsToTaskDetails(e, inline)) return
    // Radix' Typeahead im Menü darüber hört auf denselben Tasten.
    e.stopPropagation()
    if (inline) return
    // Eingabe bestätigt die Änderung — und ohne Änderung ist sie schlicht „fertig".
    if (e.key === 'Enter' && !(e.target as HTMLElement).matches('select')) {
      e.preventDefault()
      if (changed) confirm()
      else onClose()
    }
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
  }, [changed, confirm, inline, onClose])

  const repeatGroups = useMemo<PickerSelectGroup[]>(() => [
    ...(onClear ? [{ options: [{ value: NEVER, label: t('recurrence.preset.never'), icon: <CircleSlash2 size={16} /> }] }] : []),
    ...RECURRENCE_PRESET_GROUPS.map(group => ({
      label: t(GROUP_LABEL_KEY[group.tone]),
      options: group.presets.map(preset => ({ value: preset, label: t(`recurrence.preset.${preset}`), icon: PRESET_ICON[preset] })),
    })),
  ], [onClear, t])

  const showRemove = placement === 'badge' && onRemove != null
  // Steht der Kasten auf „Nie", ist schon abgenommen — und ohne Änderung gibt es nichts zu tun.
  const showConfirm = draft != null && changed

  /*
    **Bewegt wird nur in den Aufgaben-Details.** Im Popover schwebt der Kasten an einer
    gerechneten Stelle; eine wachsende Höhe schöbe ihn über seinen Anker. Nach
    `prefers-reduced-motion` fragt `AutoHeight` selbst.
  */
  const animated = inline

  const stepperLabels = { incrementLabel: t('recurrence.increment'), decrementLabel: t('recurrence.decrement') }
  const interval = draft && draft.type !== 'quota' ? draft.interval : 1

  return (
    <div
      className={`recurrence-dropdown${draft ? '' : ' recurrence-dropdown--never'}`}
      data-placement={placement}
      onKeyDown={handleKeyDown}
    >
      {/*
        Der Kasten ist hoch. In einem kurzen Fenster lag der Fuß außerhalb und war weder
        zu sehen noch zu treffen — `.context-menu-content` schneidet ab, statt zu scrollen
        (gemessen 2026-09-12: 760×420, Bestätigen bei y = 477). Also scrollt der Inhalt,
        und der Fuß bleibt stehen: Die Handlung darf nie weggerutscht sein.
      */}
      <div className="recurrence-dropdown__scroll">
        <div className="recurrence-dropdown__form">
          <div className="recurrence-dropdown__repeat">
            <Repeat size={16} className="recurrence-dropdown__repeat-icon" />
            <span className="recurrence-dropdown__muted">{t('recurrence.repeat')}</span>
            <PickerSelect
              value={draft ? presetOf(draft) : NEVER}
              onValueChange={pick}
              groups={repeatGroups}
              aria-label={t('recurrence.repeatLabel')}
              native={!inline}
              emphasized
            />
          </div>

          <AnimatePresence initial={false}>
            {draft && (
              <AutoHeight appear collapseGap={16} animated={animated} bodyClassName="recurrence-dropdown__reveal-body">
                <DecorativeWave className="recurrence-dropdown__wave" />

                <div className="recurrence-dropdown__builder">
                  {draft.type !== 'quota' && (
                    <div className="recurrence-dropdown__row">
                      <span className="recurrence-dropdown__muted">{t('recurrence.every')}</span>
                      <NumberStepper
                        value={draft.interval} min={1} max={999}
                        aria-label={t('recurrence.intervalLabel')} {...stepperLabels}
                        onValueChange={n => apply({ ...draft, interval: n })}
                      />
                      <PickerSelect
                        value={draft.unit}
                        onValueChange={unit => setUnit(unit as RecurrenceUnit)}
                        groups={[{ options: UNITS.map(u => ({ value: u, label: t(`recurrence.unit.${u}`, { count: interval }) })) }]}
                        aria-label={t('recurrence.unitLabel')}
                        native={!inline}
                      />
                    </div>
                  )}

                  {draft.type === 'fixed' && draft.unit === 'week' && (
                    <div className="recurrence-dropdown__row recurrence-dropdown__row--tight">
                      <span className="recurrence-dropdown__muted">{t('recurrence.on')}</span>
                      <div className="recurrence-dropdown__pills">
                        {WEEKDAYS.map(day => (
                          <button
                            key={day} type="button"
                            className={`recurrence-dropdown__pill${draft.weekdays.includes(day) ? ' recurrence-dropdown__pill--selected' : ''}`}
                            aria-pressed={draft.weekdays.includes(day)}
                            aria-label={weekdayName(day, locale)}
                            onClick={() => toggleWeekday(day)}
                          >
                            {weekdayName(day, locale, 'short')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {draft.type === 'fixed' && draft.unit === 'month' && draft.nthWeekday && (
                    <div className="recurrence-dropdown__row recurrence-dropdown__row--tight">
                      <span className="recurrence-dropdown__muted">{t('recurrence.on')}</span>
                      <PickerSelect
                        value={String(draft.nthWeekday.nth)}
                        onValueChange={nth => apply({ ...draft, nthWeekday: { ...draft.nthWeekday!, nth: Number(nth) as NthWeekday['nth'] } })}
                        groups={[{ options: NTHS.map(n => ({ value: String(n), label: t(`recurrence.nth.${nthKey(n)}`) })) }]}
                        aria-label={t('recurrence.nthLabel')}
                        native={!inline}
                      />
                      <PickerSelect
                        value={String(draft.nthWeekday.weekday)}
                        onValueChange={day => apply({ ...draft, nthWeekday: { ...draft.nthWeekday!, weekday: Number(day) as Weekday } })}
                        groups={[{ options: WEEKDAYS.map(d => ({ value: String(d), label: weekdayName(d, locale) })) }]}
                        aria-label={t('recurrence.weekdayLabel')}
                        native={!inline}
                      />
                    </div>
                  )}

                  {draft.type === 'fixed' && draft.unit === 'month' && !draft.nthWeekday && (
                    <div className="recurrence-dropdown__row recurrence-dropdown__row--tight">
                      <span className="recurrence-dropdown__muted">{t('recurrence.on')}</span>
                      <NumberStepper
                        arrows={false} value={draft.monthDay} min={1} max={31}
                        aria-label={t('recurrence.monthDayLabel')} {...stepperLabels}
                        onValueChange={n => apply({ ...draft, monthDay: n })}
                      />
                      <span className="recurrence-dropdown__muted">{t('recurrence.dayOfMonth')}</span>
                    </div>
                  )}

                  {draft.type === 'fixed' && draft.unit === 'year' && (
                    <div className="recurrence-dropdown__row recurrence-dropdown__row--tight">
                      <span className="recurrence-dropdown__muted">{t('recurrence.on')}</span>
                      <NumberStepper
                        arrows={false} value={draft.yearDay} min={1} max={31}
                        aria-label={t('recurrence.yearDayLabel')} {...stepperLabels}
                        onValueChange={n => apply({ ...draft, yearDay: n })}
                      />
                      <PickerSelect
                        value={String(draft.yearMonth)}
                        onValueChange={month => apply({ ...draft, yearMonth: parseInt(month, 10) })}
                        groups={[{ options: MONTHS.map(m => ({ value: String(m), label: monthName(m, locale) })) }]}
                        aria-label={t('recurrence.yearMonthLabel')}
                        native={!inline}
                      />
                    </div>
                  )}

                  {draft.type === 'quota' && (
                    <div className="recurrence-dropdown__row">
                      <NumberStepper
                        value={draft.count} min={1} max={999}
                        aria-label={t('recurrence.countLabel')} {...stepperLabels}
                        onValueChange={n => apply({ ...draft, count: n })}
                      />
                      <span className="recurrence-dropdown__muted">{t('recurrence.timesPer')}</span>
                      <PickerSelect
                        value={draft.period}
                        onValueChange={period => apply({ ...draft, period: period as QuotaPeriod })}
                        groups={[{ options: PERIODS.map(p => ({ value: p, label: t(`recurrence.period.${p}`) })) }]}
                        aria-label={t('recurrence.periodLabel')}
                        native={!inline}
                      />
                    </div>
                  )}
                </div>

                <DecorativeWave className="recurrence-dropdown__wave" />

                <div className="recurrence-dropdown__row recurrence-dropdown__row--tight">
                  <span className="recurrence-dropdown__muted">{t('recurrence.end')}</span>
                  <PickerSelect
                    value={draft.end?.kind ?? NEVER}
                    onValueChange={setEndKind}
                    groups={[{ options: END_KINDS.map(k => ({ value: k, label: t(`recurrence.endKind.${k}`) })) }]}
                    aria-label={t('recurrence.endLabel')}
                    native={!inline}
                  />
                  {draft.end?.kind === 'count' && (
                    <>
                      <NumberStepper
                        value={draft.end.remaining} min={1} max={999}
                        aria-label={t('recurrence.endCountLabel')} {...stepperLabels}
                        onValueChange={n => apply(withEnd(draft, { kind: 'count', remaining: n }))}
                      />
                      <span className="recurrence-dropdown__muted">{t('recurrence.endTimes')}</span>
                    </>
                  )}
                  {draft.end?.kind === 'date' && (
                    <DatePickerField
                      value={draft.end.date}
                      min={seed}
                      portal={inline}
                      aria-label={t('recurrence.endDateLabel')}
                      onValueChange={date => apply(withEnd(draft, { kind: 'date', date }))}
                    />
                  )}
                </div>
              </AutoHeight>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence initial={false}>
          {draft && (
            <AutoHeight appear collapseGap={8} animated={animated}>
              <div
                className={`recurrence-dropdown__summary recurrence-dropdown__summary--${recurrenceTone(draft)}`}
                data-testid="recurrence-summary"
                aria-live="polite"
              >
                <Info size={16} className="recurrence-dropdown__summary-icon" />
                <span>{describeRecurrence(draft, t, locale)}</span>
              </div>
            </AutoHeight>
          )}
        </AnimatePresence>
      </div>

      {/* In den Aufgaben-Details gibt es keinen Fuß: Geschrieben ist schon, und der Weg
          zurück ist „Nie" im Auswahlfeld (ADR 0026; CONTEXT.md, Wiederkehr-Wähler).
          Im Popover steht er nur da, wenn er etwas trägt — den Papierkorb der Marke oder
          ein „Bestätigen", das etwas zu bestätigen hat. */}
      {!inline && (showRemove || showConfirm) && (
        <div className="recurrence-dropdown__footer">
          {showRemove && (
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>{t('recurrence.remove')}</Button>
          )}
          {showConfirm && (
            <Button variant="primary" shortcut={{ kind: 'return' }} disabled={!valid} onClick={confirm}>
              {t('recurrence.confirm')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
